import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus } from "lucide-react";
import { apiClient } from "../../../lib/api";
import { useI18n } from "../../../lib/i18n";
import { toast } from "sonner";
import { DraftsList } from "../../drafts/DraftsList";

const STATUS_TABS = [
  { key: "",         labelAr: "الكل",     labelEn: "All"      },
  { key: "active",   labelAr: "نشط",       labelEn: "Active"   },
  { key: "draft",    labelAr: "مسودّة",    labelEn: "Draft"    },
  { key: "sold",     labelAr: "مُباع",      labelEn: "Sold"     },
  { key: "expired",  labelAr: "منتهي",     labelEn: "Expired"  },
  { key: "rejected", labelAr: "مرفوض",     labelEn: "Rejected" },
  { key: "archived", labelAr: "مؤرشف",     labelEn: "Archived" },
];

export default function DashListings({ businessId }) {
  const { lang } = useI18n();
  const [status, setStatus] = useState("");
  const [data, setData] = useState({ total: 0, items: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiClient.get(`/platform/dashboard/business/${businessId}/listings`, { params: status ? { status } : {} })
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch(() => toast.error(lang === "ar" ? "تعذّر تحميل الإعلانات" : "Failed to load"))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [businessId, status, lang]);

  return (
    <div className="space-y-5" data-testid="dash-listings">
      <DraftsList businessId={businessId} lang={lang} />
      <div className="flex items-center gap-2 flex-wrap">
        {STATUS_TABS.map((t) => (
          <button
            type="button"
            key={t.key || "all"}
            onClick={() => setStatus(t.key)}
            className={`px-3.5 py-1.5 rounded-full text-[12px] font-semibold transition-colors ${status === t.key ? "bg-[#0a0a0a] text-[#f7eecd]" : "bg-white border border-[#0a0a0a]/10 text-[#525252] hover:text-[#0a0a0a]"}`}
            data-testid={`listing-tab-${t.key || "all"}`}
          >
            {lang === "ar" ? t.labelAr : t.labelEn}
          </button>
        ))}
        <div className="ms-auto text-[12px] text-[#8a8a8a]">{data.total} {lang === "ar" ? "إعلان" : "listings"}</div>
        {/* Phase 2F — add-vehicle CTA for inventory businesses */}
        <Link
          to={`/sell?business_id=${businessId}`}
          className="inline-flex items-center gap-1.5 rounded-full h-9 px-4 text-[12px] font-bold shadow-sm"
          style={{ background: "linear-gradient(135deg,#e8c873,#c9a55c)", color: "#0a0a0a" }}
          data-testid="listings-add-vehicle"
        >
          <Plus className="w-4 h-4" /> {lang === "ar" ? "إضافة إعلان مركبة" : "Add vehicle listing"}
        </Link>
      </div>

      {loading ? (
        <SkeletonRows />
      ) : data.items.length === 0 ? (
        <EmptyState labelAr="لا توجد إعلانات" labelEn="No listings yet" />
      ) : (
        <div className="rounded-2xl bg-white border border-[#0a0a0a]/[0.06] overflow-hidden">
          <div className="divide-y divide-[#0a0a0a]/[0.06]">
            {data.items.map((v) => (
              <a key={v.id} href={`/vehicle/${v.id}`} className="flex items-center gap-3 p-3 hover:bg-[#fafaf7] transition-colors">
                <div className="w-16 h-12 rounded-lg bg-[#f0eee7] overflow-hidden shrink-0">
                  {v.images?.[0] && <img src={v.images[0]} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-[#0a0a0a] truncate">
                    {v.title || `${v.year || ""} ${v.make || ""} ${v.model || ""}`.trim()}
                  </div>
                  <div className="text-[11px] text-[#8a8a8a] flex items-center gap-2 mt-0.5">
                    <span className="inline-flex px-1.5 py-0.5 rounded bg-[#f0eee7]">{v.status || "—"}</span>
                    <span>·</span>
                    <span>{v.listing_type}</span>
                    {v.is_featured && <><span>·</span><span className="text-[#8a6f2e] font-semibold">{lang === "ar" ? "مميّز" : "Featured"}</span></>}
                  </div>
                </div>
                <div className="hidden sm:block text-end shrink-0">
                  <div className="text-[13px] font-bold text-[#0a0a0a]">{v.price_sar ? `${Number(v.price_sar).toLocaleString()} SAR` : "—"}</div>
                  <div className="text-[10px] text-[#8a8a8a]">{v.view_count || 0} {lang === "ar" ? "مشاهدة" : "views"}</div>
                </div>
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function SkeletonRows() {
  return (
    <div className="rounded-2xl bg-white border border-[#0a0a0a]/[0.06]">
      {[...Array(4)].map((_, i) => (
        <div key={`sk-${i}`} className="flex items-center gap-3 p-3 border-b last:border-b-0 border-[#0a0a0a]/[0.06]">
          <div className="w-16 h-12 rounded-lg bg-[#f0eee7] animate-pulse" />
          <div className="flex-1">
            <div className="w-48 h-3 bg-[#f0eee7] rounded animate-pulse mb-2" />
            <div className="w-32 h-2 bg-[#f0eee7] rounded animate-pulse" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function EmptyState({ labelAr, labelEn }) {
  const { lang } = useI18n();
  return (
    <div className="rounded-2xl bg-white border border-dashed border-[#0a0a0a]/10 p-10 text-center text-[#8a8a8a]" data-testid="empty-state">
      {lang === "ar" ? labelAr : labelEn}
    </div>
  );
}
